import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSearch } from "../context/searchcontext";
import { useUI } from '../context/uicontext'
// import SearchBar from "./searchbar";
import HeroSearchBar from "../components/herosearchbar";
import { buildSlugPath } from "../utils/slugutils";
import SEO from './SEO';
import { useAuth } from '../context/authcontext';

// ─── LUCIDE ICONS ─────────────────────────────────────────────────────────────
import {
  MapPin, ChevronDown, Search, ShieldCheck, Star, BadgeIndianRupee,
  ChevronLeft, ChevronRight, Flame,
  Bookmark, ArrowRight, ArrowUpRight, Sparkles,
  Camera, CalendarCheck, MessageSquareQuote,
  MessagesSquare, KeyRound,
  BadgePercent, MessageCircle, IndianRupee,
  Users, UserRound, Home as HomeIcon, Wallet, Crown,
  Bell, Zap, Smartphone, Check,
  Mail, BellRing,
} from "lucide-react";

// ─── GOOGLE PLAY ICON ─────────────────────────────────────────────────────────
function GooglePlayIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 40 44" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="gp1" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#00C3FF"/><stop offset="1" stopColor="#1BE2FA"/></linearGradient>
        <linearGradient id="gp2" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FFCE00"/><stop offset="1" stopColor="#FFEA00"/></linearGradient>
        <linearGradient id="gp3" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#DE3F30"/><stop offset="1" stopColor="#FF6B5C"/></linearGradient>
        <linearGradient id="gp4" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#11D574"/><stop offset="1" stopColor="#01F176"/></linearGradient>
      </defs>
      <path fill="url(#gp1)" d="M2 3.2C2 2 2.6 1 3.7.6L23.5 22 3.7 43.4C2.6 43 2 42 2 40.8V3.2z"/>
      <path fill="url(#gp2)" d="M30.7 15.4l-7.2 6.6 7.2 6.6 7.6-4.3c1.5-.9 1.5-3 0-3.9l-7.6-5z"/>
      <path fill="url(#gp3)" d="M3.7.6c.5-.2 1.1-.2 1.7.1l25.3 14.7-7.2 6.6L3.7.6z"/>
      <path fill="url(#gp4)" d="M3.7 43.4l19.8-21.4 7.2 6.6L5.4 43.3c-.6.3-1.2.3-1.7.1z"/>
    </svg>
  );
}

// ─── APP STORE ICON ───────────────────────────────────────────────────────────
function AppStoreIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

// ─── STORE TOGGLE ─────────────────────────────────────────────────────────────
function StoreToggle({ value, onChange, compact = false }) {
  const isPlay = value === "play";
  return (
    <div className={`apg-store-toggle ${compact ? "apg-store-toggle--compact" : ""}`}>
      <span className="apg-store-toggle__thumb" style={{ transform: isPlay ? "translateX(0)" : "translateX(100%)" }} />
      <button className={`apg-store-toggle__btn ${isPlay ? "apg-store-toggle__btn--on" : ""}`} onClick={() => onChange("play")}>
        <GooglePlayIcon className="apg-store-icon apg-store-icon--play" /> Play Store
      </button>
      <button className={`apg-store-toggle__btn ${!isPlay ? "apg-store-toggle__btn--on" : ""}`} onClick={() => onChange("app")}>
        <AppStoreIcon className="apg-store-icon" /> App Store
      </button>
    </div>
  );
}

// ─── DATA ─────────────────────────────────────────────────────────────────────
const LOCATIONS = [
  { name: "Kamla Nagar", count: "120+ PGs", img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=460&q=65&dpr=2" },
  { name: "Karol Bagh",   count: "216+ PGs", img: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=460&q=65&dpr=2" },
  { name: "Mukherjee Nagar",  count: "300+ PGs", img: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=460&q=65&dpr=2" },
  { name: "Saket",        count: "180+ PGs", img: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=460&q=65&dpr=2" },
  { name: "Rohini",       count: "240+ PGs", img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=460&q=65&dpr=2" },
  { name: "Dwarka",       count: "275+ PGs", img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=460&q=65&dpr=2" },
];

const PGS = [
  { id: 8747, name: "E-107 Premium Stayz", area: "Kamla Nagar", city: "New Delhi", state: "Delhi", type: "Boys", price: "15,000", amenities: ["AC", "Almirah", "Attached Bathroom"], img: "https://ciplpgapi.azurewebsites.net/azureblob/pgowner/1784562862991-pg-1784562862983-IMG_0391.JPG.jpeg" },
  { id: 8748, name: "E-92 Premium Stayz", area: "Kamla Nagar", city: "New Delhi", state: "Delhi", type: "Boys", price: "15000", amenities: ["24x7 Water Supply", "AC", "Almirah"], img: "https://ciplpgapi.azurewebsites.net/azureblob/pgowner/1784563181229-pg-1784563181219-WhatsApp Image 2026-07-20 at 9.24.33 PM.jpeg" },
  { id: 5407, name: "Boys Town PG/Paying Guest", area: "Kamla Nagar", city: "New Delhi", state: "Delhi", type: "Boys", price: "10000", amenities: ["AC", "Air Cooler", "Almirah"], img: "https://ciplpgapi.azurewebsites.net/azureblob/pgowner/1784793401584-pg-1784793401579-f3ebcae2-2326-43e8-b603-86e33ba8e762.jpg" },
  { id: 8749, name: "Girls Town PG", area: "Kamla Nagar", city: "New Delhi", state: "Delhi", type: "Girls", price: "15000", amenities: ["AC", "Almirah", "CCTV"], img: "https://ciplpgapi.azurewebsites.net/azureblob/pgowner/1784559871015-pg-1784559870996-WhatsApp Image 2026-07-20 at 8.30.28 PM.jpeg" },
  { id: 8751, name: "Dream living Homes", area: "Kamla Nagar", city: "New Delhi", state: "Delhi", type: "Co-ed", price: "10000", amenities: ["AC", "Almirah", "24x7 Water Supply"], img: "https://ciplpgapi.azurewebsites.net/azureblob/pgowner/1784729809943-pg-1784729809928-WhatsApp Image 2026-07-16 at 10.36.41 AM (1).jpeg" },
];

const WHY_ITEMS = [
  { Icon: ShieldCheck,    title: "Verified Listings",     desc: "Every PG personally verified by our team for safety & accuracy." },
  { Icon: BadgePercent,   title: "Zero Brokerage",        desc: "No middlemen, no hidden fees. What you see is what you pay." },
  { Icon: MessageCircle,  title: "Instant Owner Connect", desc: "Reach out to verified owners directly — schedule visits in seconds." },
  { Icon: IndianRupee,    title: "Best Price Guarantee",  desc: "Find a lower price elsewhere? We'll match it, every time." },
];

const HOW_STEPS = [
  { n: 1, Icon: Search,         title: "Search PG",             desc: "Filter by location, budget, gender & amenities." },
  { n: 2, Icon: MessagesSquare, title: "Chat / Schedule Visit", desc: "Connect with owners and schedule a visit instantly." },
  { n: 3, Icon: KeyRound,       title: "Book Instantly",        desc: "Pay token amount online and move in." },
];

const FEATURES = [
  { Icon: Camera,             title: "Real Photos & Videos",  desc: "No misleading shots — see every PG exactly as it is",   iconBg: "bg-feat-1", iconColor: "text-primary" },
  { Icon: Sparkles,           title: "Smart Recommendations", desc: "AI-matched stays based on your budget and lifestyle",    iconBg: "bg-feat-2", iconColor: "text-feat-2" },
  { Icon: CalendarCheck,      title: "Easy Booking",          desc: "Pay token, sign digitally, and move in — all online",   iconBg: "bg-feat-3", iconColor: "text-feat-3" },
  { Icon: MessageSquareQuote, title: "Verified Reviews",      desc: "Honest feedback from residents who actually stayed",     iconBg: "bg-feat-4", iconColor: "text-feat-4" },
];

// ─── CATEGORIES — updated to Boys/Girls/Coed/Single Sharing/Double Sharing ──
// gender matches GENDER_OPTIONS in searchbar: 'Boys' | 'Girls' | 'Co-ed'
// sharing matches SHARING_OPTIONS in searchbar: 'Single' | 'Double'
const CATS = [
  {
    label: "Boys PG",
    count: "3,200+ stays",
    img: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1200&q=75",
    Icon: Users,
    overlay: "linear-gradient(to top,rgba(23,37,84,.85),rgba(23,37,84,.35) 45%,transparent 75%)",
    gender: "Boys",
    sharing: null,
  },
  {
    label: "Girls PG",
    count: "2,800+ stays",
    img: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=75",
    Icon: UserRound,
    overlay: "linear-gradient(to top,rgba(131,24,67,.85),rgba(131,24,67,.35) 45%,transparent 75%)",
    gender: "Girls",
    sharing: null,
  },
  {
    label: "Coed",
    count: "1,500+ stays",
    img: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=75",
    Icon: HomeIcon,
    overlay: "linear-gradient(to top,rgba(6,78,59,.85),rgba(6,78,59,.35) 45%,transparent 75%)",
    gender: "Co-ed",
    sharing: null,
  },
  {
    label: "Single Sharing",
    count: "4,100+ stays",
    img: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=75",
    Icon: Wallet,
    overlay: "linear-gradient(to top,rgba(120,53,15,.85),rgba(120,53,15,.35) 45%,transparent 75%)",
    gender: null,
    sharing: "Single",
  },
  {
    label: "Double Sharing",
    count: "900+ stays",
    img: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1200&q=75",
    Icon: Crown,
    overlay: "linear-gradient(to top,rgba(76,29,149,.85),rgba(76,29,149,.35) 45%,transparent 75%)",
    gender: null,
    sharing: "Double",
  },
];

const TESTIMONIALS = {
  Students: [
    { name: "Aarav Sharma", role: "DU Student, North Campus", quote: "Found my PG in two days. The verified photos matched 100% — no surprises on move-in day.", img: "https://i.pravatar.cc/120?img=12" },
    { name: "Priya Verma",  role: "MBBS, AIIMS",              quote: "Loved that I could message owners directly. Saved me from ten broker calls a day.",            img: "https://i.pravatar.cc/120?img=47" },
    { name: "Rohan Mehta",  role: "IIT Aspirant",             quote: "Budget-friendly PG with meals, exactly as advertised. AapkaPG is a lifesaver.",               img: "https://i.pravatar.cc/120?img=33" },
  ],
  "Working Professionals": [
    { name: "Sneha Iyer",  role: "Software Engineer", quote: "Premium co-living near my office, booked online in 20 minutes. Zero brokerage was the cherry on top.", img: "https://i.pravatar.cc/120?img=49" },
    { name: "Karan Singh", role: "Product Manager",   quote: "Tried other platforms — none come close to the verified listings here.",                               img: "https://i.pravatar.cc/120?img=15" },
    { name: "Neha Kapoor", role: "Designer",           quote: "Clean PG, friendly owner, and the support team actually picks up the phone.",                         img: "https://i.pravatar.cc/120?img=44" },
  ],
  Owners: [
    { name: "Mr. Anil Gupta",  role: "Owner, Saket",  quote: "Listed for free and got 6 enquiries the same week. Tenants come pre-screened.",  img: "https://i.pravatar.cc/120?img=68" },
    { name: "Mrs. Sunita Rao", role: "Owner, Rohini", quote: "Dashboard makes it easy to manage visits. Highly recommend to fellow owners.",    img: "https://i.pravatar.cc/120?img=56" },
    { name: "Mr. Rakesh Jain", role: "Owner, Dwarka", quote: "Ten-year vacancy headache solved in two weeks. Genuine platform.",               img: "https://i.pravatar.cc/120?img=60" },
  ],
};

const FAQS = [
  { q: "Is it really no brokerage?",      a: "Yes — 100%. We don't charge tenants any brokerage fee. Owners list for free too." },
  { q: "Are PGs verified?",               a: "Every listing is physically verified by our team for safety, amenities, and accuracy of photos." },
  { q: "Can I book online?",              a: "Absolutely. Pay a small refundable token, sign a digital agreement, and your room is locked." },
  { q: "Is food included?",               a: "Many of our PGs include 2–3 meals daily. Use the 'Meals' filter to see only PGs with food." },
  { q: "What if I need to cancel?",       a: "Token amount is fully refundable up to 48 hours before your move-in date." },
  { q: "How do I connect with an owner?", a: "Tap 'Chat' on any listing to reach the owner directly through our in-app messaging — no waiting, no forms." },
];

const STORE_URLS = {
  play: "https://play.google.com/store/apps/details?id=com.aapkapg",
  app:  "https://apps.apple.com/app/aapkapg/id000000000",
};

const APP_PERKS = [
  { Icon: Bell,        label: "Real-time PG alerts" },
  { Icon: Zap,         label: "One-tap booking" },
  { Icon: ShieldCheck, label: "Verified owners only" },
  { Icon: Sparkles,    label: "AI roommate match" },
];

// ─── HELPER — build listings URL from filters (slug-based, SEO-friendly) ──────
function buildListingsUrl({ location = '', gender = '', sharing = '', price = '' }) {
  return buildSlugPath(location, gender, price, sharing);
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section id="home" className="apg-hero">
      <div className="apg-hero__blob-warm" />
      <div className="apg-hero__blob-orb" />

      <div className="apg-container apg-hero__grid">
        {/* LEFT */}
        <div className="apg-hero__left apg-fade-up">
          <span className="apg-badge">
            <span className="apg-badge__dot" />
            India's most trusted PG platform
          </span>

          <h1 className="apg-hero__headline">
            Find <span className="apg-text-primary">Verified PGs</span> Near You Without the Hassle
          </h1>

          <p className="apg-hero__sub">
            Affordable, safe, and 100% no-broker stays. Browse real photos, connect with owners, and book in minutes.
            <img src="/assets/start-search-arrow.png" alt="" />
          </p>

          {/* SearchBar with proper wrapper */}
          <div className="apg-hero__mobile-search">
            <HeroSearchBar />
          </div>

          {/* Trust strip */}
          <div className="apg-hero__trust">
            <span className="apg-trust-item"><ShieldCheck className="apg-trust-icon" /> 12,500+ Verified PGs</span>
            {/* <span className="apg-trust-item"><Star className="apg-trust-icon apg-trust-icon--fill" /> 4.8/5 from 22k renters</span> */}
            <span className="apg-trust-item"><BadgeIndianRupee className="apg-trust-icon" /> Zero brokerage</span>
          </div>
        </div>

        {/* RIGHT - Image section */}
        <div className="apg-hero__right apg-fade-up apg-fade-up--delay">
          <div className="apg-hero__img-wrap">
            <div className="apg-hero__img-glow" />
            <img src="/assets/hero-pg-room.webp" alt="Modern PG room" className="apg-hero__img" fetchpriority="high" />

            <div className="apg-float-badge apg-float-badge--tl apg-anim-float">
              <div className="apg-float-badge__icon apg-float-badge__icon--green">
                <ShieldCheck className="apg-fb-icon" />
              </div>
              <div>
                <div className="apg-float-badge__micro">Status</div>
                <div className="apg-float-badge__main">Verified ✓</div>
              </div>
            </div>

            <div className="apg-float-badge apg-float-badge--mr apg-float-badge--primary apg-anim-float" style={{ animationDelay: "1s" }}>
              <div className="apg-float-badge__micro">Starting</div>
              <div className="apg-float-badge__price">₹6,500/month</div>
            </div>

            <div className="apg-float-badge apg-float-badge--bl apg-anim-float" style={{ animationDelay: "2s" }}>
              <div className="apg-float-badge__icon apg-float-badge__icon--orange">
                <Star className="apg-fb-icon apg-fb-icon--fill" />
              </div>
              <div>
                <div className="apg-float-badge__main">4.8 ★</div>
                <div className="apg-float-badge__micro">2.4k reviews</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SelectField({ icon, label, value }) {
  return (
    <button className="apg-select-field">
      <div className="apg-select-field__left">
        {icon && <span className="apg-select-field__icon">{icon}</span>}
        <div>
          <div className="apg-select-field__label">{label}</div>
          <div className="apg-select-field__value">{value}</div>
        </div>
      </div>
      <ChevronDown className="apg-select-chevron" />
    </button>
  );
}

// ─── POPULAR LOCATIONS ────────────────────────────────────────────────────────
// Clicking a card → sets location in SearchContext → navigates to /listings
function PopularLocations() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { setLocation, setSearchResults, setSearchCoordinates, setNearbyPincodes } = useSearch();
  const [isLoading, setIsLoading] = useState(false);

  const scroll = (d) => ref.current?.scrollBy({ left: d * 360, behavior: "smooth" });

  const handleLocationClick = async (name) => {
    setIsLoading(true);
    
    // Save to localStorage for refresh
    localStorage.setItem('lastSearchTerm', name);
    localStorage.setItem('lastSearchGender', '');
    localStorage.setItem('lastSearchPrice', '');
    localStorage.setItem('lastSearchSharing', '');
    
    // Clear previous results
    setSearchResults([]);
    setSearchCoordinates(null);
    setNearbyPincodes([]);
    
    // Set location in context
    setLocation(name);
    
    // Navigate to listings page with the location
    navigate(buildListingsUrl({ location: name }));
    
    setIsLoading(false);
  };

  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-section-header">
          <div>
            <div className="apg-eyebrow"><Flame className="apg-ey-icon" /> Trending in Delhi NCR</div>
            <h2 className="apg-section-title">Popular Locations</h2>
            <p className="apg-section-sub">Discover PGs in the city's most loved neighbourhoods</p>
          </div>
          <div className="apg-section-header__end apg-hide-mobile">
            <button onClick={() => scroll(-1)} className="apg-arrow-btn apg-hide-mobile"><ChevronLeft className="apg-arr-icon" /></button>
            <button onClick={() => scroll(1)} className="apg-arrow-btn apg-hide-mobile"><ChevronRight className="apg-arr-icon" /></button>
          </div>
        </div>

        <div ref={ref} className="apg-hscroll">
          {LOCATIONS.map(l => (
            <div
              key={l.name}
              className="apg-loc-card"
              onClick={() => handleLocationClick(l.name)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleLocationClick(l.name)}
            >
              <img src={l.img} alt={l.name} loading="lazy" className="apg-loc-card__img" />
              <div className="apg-loc-card__overlay" />
              <div className="apg-loc-card__body">
                <div className="apg-loc-card__name">{l.name}</div>
                <div className="apg-loc-card__count">{l.count}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TRENDING PGS ─────────────────────────────────────────────────────────────
// Clicking the card or "View Details" → navigate to /pg/:slug (PG detail page)
function TrendingPGs() {
  const ref      = useRef(null);
  const navigate = useNavigate();
  const [saved, setSaved] = useState({});

  const scroll = (d) => ref.current?.scrollBy({ left: d * 360, behavior: "smooth" });

  const goToDetail = (p) => {
    // Replace spaces, slashes, and other special characters
    const slug = p.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')  // Replace any non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
    navigate(`/pg/${slug}-${p.id}`, { state: { PGNAME: p.name, PGID: p.id } });
  };

  return (
    <section id="trending" className="apg-section apg-section--cream">
      <div className="apg-container">
        <div className="apg-section-header">
          <div>
            <div className="apg-eyebrow--plain">Handpicked for you</div>
            <h2 className="apg-section-title">Trending PGs Near You</h2>
            <p className="apg-section-sub">Top-rated stays our community is booking right now</p>
          </div>
          <div className="apg-section-header__end apg-hide-mobile">
            <button onClick={() => scroll(-1)} className="apg-arrow-btn"><ChevronLeft className="apg-arr-icon" /></button>
            <button onClick={() => scroll(1)}  className="apg-arrow-btn"><ChevronRight className="apg-arr-icon" /></button>
          </div>
        </div>

        <div ref={ref} className="apg-hscroll">
          {PGS.map(p => {
            const isSaved = !!saved[p.name];
            return (
              <article
                key={p.name}
                className="apg-pg-card"
                onClick={() => goToDetail(p)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && goToDetail(p)}
              >
                <img className="verified-badge" src="/assets/verified-badge.webp" alt="" />
                <div className="apg-pg-card__img-wrap">
                  <img src={p.img} alt={p.name} loading="lazy" className="apg-pg-card__img" />
                  <span className={`apg-pg-card__type gender-${p.type.toLowerCase()}`}>{p.type}</span>
                  <button
                    className="apg-pg-card__bm"
                    onClick={(e) => { e.stopPropagation(); setSaved(s => ({ ...s, [p.name]: !s[p.name] })); }}
                    aria-label="Bookmark"
                  >
                    <Bookmark className={`apg-bm-icon ${isSaved ? "apg-bm-icon--saved" : ""}`} />
                  </button>
                </div>
                <div className="apg-pg-card__body">
                  {/* <img className="rating-badge" src="/assets/rating-badge.png" alt="" /> */}
                  <h3 className="apg-pg-card__name">{p.name}</h3>
                  <div className="apg-pg-card__loc">
                    <MapPin className="apg-loc-icon" />
                    <span><strong>{p.area}</strong>, {p.state}</span>
                  </div>
                  <div className="apg-amenities">{p.amenities.map(a => <span key={a} className="apg-chip">{a}</span>)}</div>
                  <div className="apg-pg-card__footer">
                    <div><span className="apg-pg-card__price">₹{p.price}</span><span className="apg-pg-card__per"> /month</span></div>
                    <button
                      className="apg-btn apg-btn--primary apg-btn--sm apg-btn--rx"
                      onClick={(e) => { e.stopPropagation(); goToDetail(p); }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">Why AapkaPG</div>
          <h2 className="apg-section-title">A renting experience you'll actually enjoy</h2>
          <p className="apg-section-sub apg-section-sub--center">Designed around real renters — not brokers.</p>
        </div>
        <div className="apg-why-grid">
          {WHY_ITEMS.map(it => (
            <div key={it.title} className="apg-why-card">
              <div className="apg-why-card__icon"><it.Icon className="apg-why-icon" /></div>
              <h3 className="apg-why-card__title">{it.title}</h3>
              <p className="apg-why-card__desc">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="apg-section apg-section--cream">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">How it works</div>
          <h2 className="apg-section-title">Get a PG in 3 simple steps</h2>
        </div>
        <div className="apg-steps">
          <div className="apg-steps__line" />
          {HOW_STEPS.map(s => (
            <div key={s.n} className="apg-step">
              <div className="apg-step__circle">
                <s.Icon className="apg-step-icon" />
                <span className="apg-step__num">{s.n}</span>
              </div>
              <h3 className="apg-step__title">{s.title}</h3>
              <p className="apg-step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OwnerBanner() {
  const { setShowAuth, showOwnerAlert, setShowOwnerAlert } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();

    const handleListPGClick = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    // Check user type instead of role
    if (user.type === 'pg') {
      navigate('/owner-dashboard');
    } else {
      setShowOwnerAlert(true);
    }
  };

  return (
    <>
      <section id="owners" className="apg-section">
        <div className="apg-container">
          <div className="apg-owner-banner">
            <div className="apg-owner-banner__deco" />
            <div className="apg-owner-banner__glow" />
            <div className="apg-owner-banner__grid">
              <div>
                <div className="apg-badge apg-badge--glass">
                  <Sparkles className="apg-badge-icon" /> For PG Owners
                </div>
                <h2 className="apg-owner-banner__title">Are You a <span className="apg-text-primary">PG Owner?</span></h2>
                <p className="apg-owner-banner__desc">List your property in 5 minutes and reach 50,000+ verified tenants searching every month. Free to start, no commission.</p>
                <div className="apg-owner-banner__perks">
                  <span>✓ Free listing</span><span>✓ Verified tenants only</span><span>✓ Dashboard insights</span>
                </div>
              </div>
              <div className="apg-owner-banner__cta-wrap">
                <button
                  className="apg-btn apg-btn--primary apg-btn--lg apg-btn--rx apg-btn--glow"
                  onClick={handleListPGClick}
                >
                  List Your PG <ArrowRight className="apg-btn-icon" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showOwnerAlert && (
        <div className="apg-modal-overlay" onClick={() => setShowOwnerAlert(false)}>
          <div className="apg-modal" onClick={(e) => e.stopPropagation()}>
            <button className="apg-modal__close" onClick={() => setShowOwnerAlert(false)}>✕</button>
            <div className="apg-modal__icon">🏠</div>
            <h3 className="apg-modal__title">Owner Access Required</h3>
            <p className="apg-modal__desc">
              {user ? 'You\'re signed in as a tenant. Register as an owner to list your PG.' : 'Register as an owner to list your PG.'}
            </p>
            <div className="apg-modal__actions">
              <button className="apg-modal__btn apg-modal__btn--primary" onClick={() => { setShowOwnerAlert(false); setShowAuth(true); }}>
                Register as Owner
              </button>
              <button className="apg-modal__btn apg-modal__btn--secondary" onClick={() => setShowOwnerAlert(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Features() {
  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-center-block">
          <h2 className="apg-section-title">Our Features</h2>
          <p className="apg-features__sub">Everything you need to discover, compare, and book your perfect PG — without the broker headaches.</p>
        </div>
        <div className="apg-features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="apg-feature-item">
              <div className={`apg-feature-item__icon apg-fi-bg--${f.iconBg} apg-fi-c--${f.iconColor}`}>
                <f.Icon className="apg-fi-icon" strokeWidth={2.2} />
              </div>
              <h3 className="apg-feature-item__title">{f.title}</h3>
              <p className="apg-feature-item__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CATEGORIES ───────────────────────────────────────────────────────────────
// Clicking a category → clears location, sets only gender or sharing → navigate to /listings
// API payload will be: { SearchText: null, PGRentRange: null, PGType: <gender>, PinCode: null }
function Categories() {
  const navigate = useNavigate();
  const { setLocation, setGender, setSharing } = useSearch();

  const handleCatClick = (cat) => {
    // Clear location — we're doing a region-wide category browse, not a locality search
    setLocation("");
    // Set gender/sharing, clear the other
    setGender(cat.gender || "");
    setSharing(cat.sharing || "");

    // buildSlugPath with empty location → "/listings"
    // Gender/sharing filters land in context; listings page will apply them
    // against the default city-wide fetch (SearchText: null, PGType: <gender>)
    navigate(buildListingsUrl({
      location: "",
      gender:   cat.gender  || "",
      sharing:  cat.sharing || "",
    }));
  };

  return (
    <section className="apg-section apg-section--cream">
      <div className="apg-container">
        <div className="apg-section-header apg-section-header--mb">
          <div>
            <div className="apg-eyebrow--plain apg-eyebrow--upper">Find what fits you</div>
            <h2 className="apg-section-title">Explore by Category</h2>
          </div>
        </div>

        {/* Hero category — first card (Boys PG) */}
        <div
          className="apg-cat-hero"
          onClick={() => handleCatClick(CATS[0])}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleCatClick(CATS[0])}
        >
          <img src={CATS[0].img} alt={CATS[0].label} loading="lazy" className="apg-cat-hero__img" />
          <div className="apg-cat-hero__overlay" style={{ background: CATS[0].overlay }} />
          <div className="apg-cat-hero__icon-wrap"><Users className="apg-cat-icon" /></div>
          <div className="apg-cat-hero__body">
            <div className="apg-cat-hero__label">{CATS[0].label}</div>
            <div className="apg-cat-hero__count">{CATS[0].count}</div>
            <div className="apg-cat-hero__cta">Explore stays <ArrowUpRight className="apg-tl-icon" /></div>
          </div>
        </div>

        {/* Remaining 4 category cards */}
        <div className="apg-cat-grid">
          {CATS.slice(1).map(c => (
            <div
              key={c.label}
              className="apg-cat-card"
              onClick={() => handleCatClick(c)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleCatClick(c)}
            >
              <img src={c.img} alt={c.label} loading="lazy" className="apg-cat-card__img" />
              <div className="apg-cat-card__overlay" style={{ background: c.overlay }} />
              <div className="apg-cat-card__icon-wrap"><c.Icon className="apg-cat-icon" /></div>
              <div className="apg-cat-card__body">
                <div className="apg-cat-card__label">{c.label}</div>
                <div className="apg-cat-card__count">{c.count}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const tabs = ["Students", "Working Professionals", "Owners"];
  const [tab, setTab] = useState("Students");
  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">Loved by thousands</div>
          <h2 className="apg-section-title">What our community says</h2>
        </div>
        <div className="apg-tabs-wrap">
          <div className="apg-tabs">
            {tabs.map(t => (
              <button key={t} onClick={() => setTab(t)} className={`apg-tab ${tab === t ? "apg-tab--active" : ""}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="apg-testimonials-grid">
          {TESTIMONIALS[tab].map(t => (
            <div key={t.name} className="apg-t-card">
              <div className="apg-t-card__stars">{[1,2,3,4,5].map(i => <Star key={i} className="apg-star-icon" />)}</div>
              <p className="apg-t-card__quote">"{t.quote}"</p>
              <div className="apg-t-card__author">
                <img src={t.img} alt={t.name} loading="lazy" className="apg-t-card__avatar" />
                <div><div className="apg-t-card__name">{t.name}</div><div className="apg-t-card__role">{t.role}</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AppDownload() {
  const [store, setStore] = useState("play");
  const isPlay = store === "play";

  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-app-grid">

          {/* LEFT — copy */}
          <div className="apg-app-copy">
            <div className="apg-badge">
              <Smartphone className="apg-badge-icon" />
              <span>Download · Sign in · Move in</span>
            </div>

            <h2 className="apg-app-copy__title">
              Your next PG is{" "}
              <span className="apg-app-copy__hl">
                <span className="apg-text-primary">one tap</span>
                <span className="apg-app-copy__hl-bar" />
              </span>{" "}away.
            </h2>

            <p className="apg-app-copy__desc">
              Get the AapkaPG app from the Play Store or App Store. Browse verified PGs, chat with owners, and book your stay — all from your phone.
            </p>

            <div className="apg-app-steps-card">
              {[
                { n: "01", t: "Tap your store",  d: "Play Store or App Store" },
                { n: "02", t: "Install the app", d: "Free, lightweight & fast" },
                { n: "03", t: "Open & book",     d: "Verified PGs await" },
              ].map(s => (
                <div key={s.n} className="apg-app-step">
                  <div className="apg-app-step__num">{s.n}</div>
                  <div className="apg-app-step__title">{s.t}</div>
                  <div className="apg-app-step__desc">{s.d}</div>
                </div>
              ))}
            </div>

            <ul className="apg-app-perks">
              {APP_PERKS.map(p => (
                <li key={p.label} className="apg-app-perk">
                  <span className="apg-app-perk__check"><Check className="apg-check-icon" strokeWidth={3} /></span>
                  {p.label}
                </li>
              ))}
            </ul>

            <div className="apg-app-proof">
              <div className="apg-app-proof__stars">
                {[1,2,3,4,5].map(i => <Star key={i} className="apg-star-icon" />)}
                <span><strong>4.8/5</strong> · 12,400+ reviews</span>
              </div>
              <div className="apg-app-proof__avs">
                {["12","47","33","49"].map(id => (
                  <img key={id} src={`https://i.pravatar.cc/64?img=${id}`} alt="" className="apg-app-proof__av" loading="lazy" />
                ))}
                <div className="apg-app-proof__more">25k+</div>
              </div>
            </div>
          </div>

          {/* RIGHT — phone mockup */}
          <div className="apg-phone-wrap">
            <div className="apg-phone-halo apg-phone-halo--1" />
            <div className="apg-phone-halo apg-phone-halo--2" />

            <div className="apg-phone-chip apg-phone-chip--left apg-anim-float">
              <div className="apg-phone-chip__icon apg-phone-chip__icon--green"><ShieldCheck className="apg-chip-icon" /></div>
              <div><div className="apg-phone-chip__title">100% Verified</div><div className="apg-phone-chip__sub">No brokerage</div></div>
            </div>
            <div className="apg-phone-chip apg-phone-chip--right apg-anim-float apg-anim-float--delay">
              <div className="apg-phone-chip__icon apg-phone-chip__icon--orange"><Smartphone className="apg-chip-icon" /></div>
              <div><div className="apg-phone-chip__title">Tap to install</div><div className="apg-phone-chip__sub">Free download</div></div>
            </div>

            <div className="apg-phone">
              <span className="apg-phone__btn apg-phone__btn--l1" />
              <span className="apg-phone__btn apg-phone__btn--l2" />
              <span className="apg-phone__btn apg-phone__btn--r1" />

              <div className="apg-phone__screen">
                <div className="apg-phone__notch" />
                <div className="apg-phone__status">
                  <span>9:41</span>
                  <span className="apg-phone__status-r">
                    <span className="apg-phone__dot" /><span>5G</span>
                    <span className="apg-phone__battery"><span /></span>
                  </span>
                </div>

                <div className="apg-phone__content">
                  <div className="apg-phone__brand">
                    <img src="/assets/AapkaPG-logo.svg" alt="" />
                  </div>

                  <div className="apg-phone__toggle-wrap">
                    <StoreToggle value={store} onChange={setStore} compact />
                  </div>

                  <div className="apg-phone__store-label">
                    Get the app on <span className="apg-text-primary">{isPlay ? "Google Play" : "App Store"}</span>
                  </div>
                  <div className="apg-phone__hint">Scan below to install</div>

                  <div className="apg-phone__qr-wrap">
                    <span className="apg-qr-corner apg-qr-corner--tl" />
                    <span className="apg-qr-corner apg-qr-corner--tr" />
                    <span className="apg-qr-corner apg-qr-corner--bl" />
                    <span className="apg-qr-corner apg-qr-corner--br" />
                    <span className="apg-qr-beam" />
                    <div className="apg-phone__qr-box">
                      <img src="/assets/qr-code.png" alt="Scan to download AapkaPG" className="apg-qr-img" />
                    </div>
                  </div>

                  <div className="apg-phone__or">
                    <span className="apg-phone__or-line" />
                    <span className="apg-phone__or-text">or tap to open</span>
                    <span className="apg-phone__or-line" />
                  </div>

                  <a href={STORE_URLS[store]} target="_blank" rel="noopener noreferrer" className="apg-phone__store-btn">
                    {isPlay
                      ? <GooglePlayIcon className={`apg-phone__store-svg ${isPlay ? "apg-phone__store-svg--play" : ""}`} />
                      : <AppStoreIcon   className="apg-phone__store-svg" />}
                    <div>
                      <div className="apg-phone__store-tiny">{isPlay ? "GET IT ON" : "Download on the"}</div>
                      <div className="apg-phone__store-main">{isPlay ? "Google Play" : "App Store"}</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            <div className="apg-phone__shadow" />
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQs() {
  const [open, setOpen] = useState(null);
  return (
    <section className="apg-section apg-section--cream">
      <div className="apg-container apg-container--narrow">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">FAQs</div>
          <h2 className="apg-section-title">Questions, answered</h2>
          <p className="apg-section-sub apg-section-sub--center">Everything you need to know before you book.</p>
        </div>
        <div className="apg-faqs">
          {FAQS.map((f, i) => (
            <div key={i} className={`apg-faq ${open === i ? "apg-faq--open" : ""}`}>
              <button className="apg-faq__trigger" onClick={() => setOpen(open === i ? null : i)}>
                <span>{f.q}</span>
                <ChevronDown className="apg-faq__chevron" />
              </button>
              {open === i && <div className="apg-faq__answer">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  return (
    <section className="apg-section">
      <div className="apg-container apg-container--wide">
        <div className="apg-newsletter">
          <div className="apg-newsletter__blob apg-newsletter__blob--1" />
          <div className="apg-newsletter__blob apg-newsletter__blob--2" />
          <div className="apg-newsletter__dots" />
          <div className="apg-newsletter__grid">
            <div>
              <div className="apg-badge apg-badge--glass"><Sparkles className="apg-badge-icon" /> Weekly drops</div>
              <h2 className="apg-newsletter__title">Get the best PG deals,<br /><span className="apg-text-primary">straight to your inbox.</span></h2>
              <p className="apg-newsletter__desc">Be first to know about new listings, price drops, and exclusive offers in your city.</p>
              <div className="apg-newsletter__meta">
                <span><BellRing className="apg-meta-icon" /> Weekly updates</span>
                <span><Mail className="apg-meta-icon" /> Zero spam</span>
              </div>
            </div>
            <div className="apg-newsletter__form-wrap">
              <form className="apg-newsletter__form" onSubmit={e => e.preventDefault()}>
                <div className="apg-newsletter__input-row">
                  <Mail className="apg-nl-icon" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@email.com" className="apg-newsletter__input" required />
                </div>
                <button type="submit" className="apg-btn apg-btn--primary apg-btn--newsletter">Subscribe</button>
              </form>
              <p className="apg-newsletter__disclaimer">Join 25,000+ renters. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
export default function Home() {
  const { clearAll } = useSearch();

  // Clear every search field the moment the user lands on the home page,
  // whether they navigated from listings, a PG detail, or anywhere else.
  useEffect(() => {
    clearAll();
  }, []);

  return (
    <>
      {/* ✅ SEO Component - Updates <head> with Home Page meta tags */}
      <SEO 
        title="Find Verified PG Accommodation Near You | Zero Brokerage | AapkaPG"
        description="Search, compare & book verified PGs across India for boys, girls, students and professionals. Real photos, zero brokerage, instant booking on AapkaPG."
        canonicalUrl="https://aapkapg.com/"
        keywords={['PG accommodation', 'PG in Delhi', 'hostel', 'rental', 'student housing', 'paying guest', 'girls PG', 'boys PG']}
      />
      
      <div className="apg-root">
        <Hero />
        <PopularLocations />
        <TrendingPGs />
        <WhyChoose />
        <HowItWorks />
        <OwnerBanner />
        <Features />
        <Categories />
        <Testimonials />
        <AppDownload />
        <FAQs />
        <Newsletter />
      </div>
    </>
  );
}